import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes, faRepeat, faCreditCard } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

function AddPaymentModal({ onClose, onPaymentAdded }) {
  const { t } = useTranslation();
  const [type, setType] = useState("recurrent");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [totalMonths, setTotalMonths] = useState("");
  const [installmentSource, setInstallmentSource] = useState("personal");
  const [currency, setCurrency] = useState("EUR");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Calculate monthly payment and end date for installments
  const installmentCalc = useMemo(() => {
    if (type !== "installment" || !totalAmount || !totalMonths) return null;
    
    const monthly = Math.ceil((parseFloat(totalAmount) / parseInt(totalMonths)) * 100) / 100;
    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + parseInt(totalMonths));
    
    return {
      monthlyAmount: monthly,
      endDate: endDate.toLocaleDateString(undefined, { month: "short", year: "numeric" }),
    };
  }, [type, totalAmount, totalMonths]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        name,
        type,
        currency,
      };

      if (type === "recurrent") {
        payload.amount = parseFloat(amount);
      } else {
        payload.totalAmount = parseFloat(totalAmount);
        payload.totalMonths = parseInt(totalMonths);
        payload.installmentSource = installmentSource;
      }

      await axios.post(`${process.env.REACT_APP_API_URL}/monthly-payments`, payload);
      onPaymentAdded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isValid = () => {
    if (!name.trim()) return false;
    if (type === "recurrent") {
      return amount && parseFloat(amount) > 0;
    } else {
      return totalAmount && parseFloat(totalAmount) > 0 && totalMonths && parseInt(totalMonths) > 0;
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200 transition-colors">
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800 transition-colors">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white">
            {t("addMonthlyPayment", "Додати платіж")}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Type Toggle */}
          <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setType("recurrent")}
              className={`flex-1 py-3 px-2 sm:px-4 flex items-center justify-center gap-1.5 sm:gap-2 font-medium text-sm sm:text-base transition-all ${
                type === "recurrent"
                  ? "bg-primary-600 text-white"
                  : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              <FontAwesomeIcon icon={faRepeat} className="text-xs sm:text-sm" />
              <span>{t("recurrent", "Регулярний")}</span>
            </button>
            <button
              type="button"
              onClick={() => setType("installment")}
              className={`flex-1 py-3 px-2 sm:px-4 flex items-center justify-center gap-1.5 sm:gap-2 font-medium text-sm sm:text-base transition-all ${
                type === "installment"
                  ? "bg-primary-600 text-white"
                  : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              <FontAwesomeIcon icon={faCreditCard} className="text-xs sm:text-sm" />
              <span>{t("installment", "Розстрочка")}</span>
            </button>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
              {t("name", "Назва")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
              placeholder={t("enterPaymentName", "Введіть назву платежу")}
              required
            />
          </div>

          {type === "recurrent" ? (
            /* Recurrent Fields */
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t("amount", "Сума")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t("currency", "Валюта")} <span className="text-red-500">*</span>
                </label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                >
                  <option value="EUR">EUR</option>
                  <option value="UAH">UAH</option>
                  <option value="USD">USD</option>
                  <option value="HUF">HUF</option>
                </select>
              </div>
            </div>
          ) : (
            /* Installment Fields */
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                    {t("totalAmount", "Загальна сума")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={totalAmount}
                    onChange={(e) => setTotalAmount(e.target.value)}
                    className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                    {t("currency", "Валюта")} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                  >
                    <option value="EUR">EUR</option>
                    <option value="UAH">UAH</option>
                    <option value="USD">USD</option>
                    <option value="HUF">HUF</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                    {t("months", "Місяців")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={totalMonths}
                    onChange={(e) => setTotalMonths(e.target.value)}
                    className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                    placeholder="12"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                    {t("source", "Джерело")}
                  </label>
                  <select
                    value={installmentSource}
                    onChange={(e) => setInstallmentSource(e.target.value)}
                    className="w-full p-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all dark:text-white"
                  >
                    <option value="personal">{t("personal", "Особисті")}</option>
                    <option value="bank">{t("bank", "Банк")}</option>
                  </select>
                </div>
              </div>

              {/* Calculation Preview */}
              {installmentCalc && (
                <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {t("monthlyPayment", "Щомісячно")}:
                      </p>
                      <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                        {installmentCalc.monthlyAmount} {currency}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {t("paidBy", "Сплачено до")}:
                      </p>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {installmentCalc.endDate}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {error && (
            <div className="text-red-500 text-center py-2 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl font-bold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t("cancel", "Скасувати")}
            </button>
            <button
              type="submit"
              disabled={loading || !isValid()}
              className="flex-1 btn-primary py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} />
                  {t("add", "Додати")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default AddPaymentModal;

